import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMeta } from "@/lib/useMeta";

const NAVY = "#0A1628";
const CARD = "#112240";
const GOLD = "#C9A84C";
const fieldStyle = { background: "rgba(255,255,255,0.04)", borderColor: "rgba(201,168,76,0.22)", color: "#EAF0F7" };

export default function ForgotPassword() {
  useMeta({ title: "Reset your password | Plan-Sarthi", description: "Request a Plan-Sarthi password reset link.", path: "/forgot-password" });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 py-12" style={{ background: NAVY, fontFamily: "Inter, system-ui, sans-serif" }} data-testid="forgot-password-page">
      <Link to="/" className="flex items-baseline mb-8">
        <span className="text-2xl font-bold tracking-tight text-white">Plan</span>
        <span className="text-2xl font-bold tracking-tight" style={{ color: GOLD }}>-</span>
        <span className="text-2xl font-bold tracking-tight text-white">Sarthi</span>
      </Link>
      <div className="w-full max-w-[420px] border p-7 sm:p-9" style={{ background: CARD, borderColor: "rgba(201,168,76,0.18)" }}>
        {sent ? (
          <div data-testid="forgot-sent-notice">
            <MailCheck className="w-8 h-8" style={{ color: GOLD }} />
            <h1 className="text-white text-2xl font-semibold mt-3">Check your email</h1>
            <p className="text-[13px] mt-2 leading-relaxed" style={{ color: "rgba(234,240,247,0.6)" }}>
              If <strong style={{ color: "#EAF0F7" }}>{email}</strong> is registered, a reset link is on its way. The link
              expires in 30 minutes and can be used once.
            </p>
            <Link to="/login" className="inline-block mt-6 text-[13px] font-medium hover:underline" style={{ color: GOLD }} data-testid="back-to-login-link">
              ← Back to login
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-white text-2xl font-semibold tracking-tight">Forgot password</h1>
            <p className="text-[13px] mt-1.5" style={{ color: "rgba(234,240,247,0.55)" }}>
              Enter your registered email and we&apos;ll send a reset link valid for 30 minutes.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-password-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "rgba(234,240,247,0.6)" }}>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={fieldStyle} data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full font-semibold" style={{ background: GOLD, color: NAVY }} disabled={busy} data-testid="forgot-submit-btn">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-block mt-5 text-[12.5px] hover:underline" style={{ color: "rgba(234,240,247,0.5)" }} data-testid="back-to-login-link">
              ← Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
